import { createEffect, createSignal, onMount, For, Show } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { playListPassword, playListUrl, playListUsername } from "../../../state";
import { Row } from "@lightningtv/solid-ui";
import gsap from "gsap";
import LoadingIndicator from "../loading/loading";
import { fixImageUrl } from "../../utils/fixImageUrl";

// The Services UI itself, usable either as a full page (see Services.tsx,
// the /services route) or embedded inline under Home's header — where
// selecting "Services" shows this without navigating away and losing the
// header row.
const ServicesPanel = (props: {
    onEscape: () => void;
    onUp?: () => void;
    // Same imperative-handle pattern as MoviesPanel's panelRef — a plain
    // object this component attaches a focusEntry() method to, so a parent
    // (Home) can send focus into this panel's content, e.g. when pressing
    // Down from the header while this panel is open.
    panelRef?: { focusEntry?: () => void };
}) => {
    const [services, setServices] = createSignal([]);
    const [loading, setLoading] = createSignal(true);
    const [error, setError] = createSignal(null);
    const [selectedService, setSelectedService] = createSignal(null);
    const [isNavigating, setIsNavigating] = createSignal(false);
    const [showLoadingScreen, setShowLoadingScreen] = createSignal(true);

    let mainContentRef;
    let loadingScreenRef;
    let heroContentRef;
    let rowRef;

    // Fetch services
    createEffect(async () => {
        try {
            setLoading(true);
            setError(null);

            const apiUrl = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_services`;

            console.log('Fetching services from:', apiUrl);

            const response = await fetch(apiUrl);
            if (!response.ok) throw new Error(`HTTP Error ${response.status}`);

            const data = await response.json();
            console.log('Services data:', data);

            setServices(data);

            // Set first service as selected by default
            if (data && data.length > 0) {
                setSelectedService(data[0]);
            }
        } catch (err) {
            console.error("Error fetching services:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    });

    // Animate loading screen
    onMount(() => {
        const timeline = gsap.timeline();

        timeline.to(loadingScreenRef, {
            alpha: 0,
            duration: 0.3,
            ease: "power2.out",
            delay: 0.4,
            onComplete: () => {
                setShowLoadingScreen(false);
            }
        });

        timeline.to(mainContentRef, {
            alpha: 1,
            duration: 0.4,
            ease: "power2.out"
        }, "-=0.15");
    });

    const handleEscape = () => {
        if (isNavigating()) return true;
        setIsNavigating(true);

        gsap.to(mainContentRef, {
            alpha: 0,
            duration: 0.25,
            ease: "power2.inOut",
            onComplete: () => {
                props.onEscape();
                setIsNavigating(false);
            }
        });

        // Stop this Escape from also bubbling up to an ancestor's onEscape —
        // matters when this panel is embedded inside Home's tree rather than
        // mounted as its own route (see the same fix on SettingsPanel).
        return true;
    };

    const handleServiceFocus = (service) => {
        setSelectedService(service);

        // Animate hero content change
        if (heroContentRef) {
            gsap.to(heroContentRef, {
                alpha: 0,
                duration: 0.2,
                ease: "power2.out",
                onComplete: () => {
                    gsap.to(heroContentRef, {
                        alpha: 1,
                        duration: 0.3,
                        ease: "power2.in"
                    });
                }
            });
        }
    };

    const getCategoryIcon = (category) => {
        const icons = {
            "Transport": "🚗",
            "Recreation": "🏊",
            "Food": "🍽️",
            "Spa": "💆",
            "Fitness": "💪",
            "Business": "💼"
        };
        return icons[category] || "📋";
    };

    return (
        <>
            {/* Loading Screen */}
            <Show when={showLoadingScreen()}>
                <View
                    ref={loadingScreenRef}
                    width={1920}
                    height={1080}
                    color={hexColor("#000000")}
                    alpha={1}
                    style={{ zIndex: 2000, position: "absolute" }}
                >
                    <View x={800} y={400}>
                        <LoadingIndicator />
                    </View>
                </View>
            </Show>

            {/* Background */}
            <View
                width={1920}
                height={1080}
                color={hexColor("#000000")}
                alpha={1}
            />

            {/* Main Content */}
            <View
                ref={mainContentRef}
                alpha={0}
                style={{ position: "absolute", zIndex: 1 }}
                onEscape={handleEscape}
            >
                <View style={{ width: 1920, height: 1080 }}>
                    {/* Hero Content — same layout language as ContentBlock (Entity page)
                        and the Movies hero: title, then details, then a short description */}
                    <View x={80} y={150} ref={heroContentRef} alpha={1}>
                        <Show when={selectedService()}>
                            <View
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 24,
                                    width: 1200,
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 72,
                                        fontWeight: 400,
                                        lineHeight: 88,
                                        color: hexColor("#ffffff"),
                                        fontFamily: "Roboto",
                                        maxLines: 1,
                                        width: 1200,
                                        contain: "width",
                                        marginBottom: 20,
                                    }}
                                >
                                    {selectedService().service_name}
                                </Text>

                                <Show when={selectedService().service_category}>
                                    <Text
                                        style={{
                                            fontSize: 28,
                                            color: hexColor("#B9B9BE"),
                                            fontFamily: "Roboto",
                                            maxLines: 1,
                                        }}
                                    >
                                        {getCategoryIcon(selectedService().service_category)} {selectedService().service_category}
                                    </Text>
                                </Show>

                                <Show when={selectedService().short_service_description}>
                                    <Text
                                        contain="width"
                                        style={{
                                            fontSize: 28,
                                            lineHeight: 40,
                                            width: 800,
                                            maxLines: 4,
                                            color: hexColor("#B9B9BE"),
                                            fontFamily: "Roboto",
                                        }}
                                    >
                                        {selectedService().short_service_description}
                                    </Text>
                                </Show>

                                <Show when={selectedService().service_description}>
                                    <Text
                                        contain="width"
                                        style={{
                                            fontSize: 22,
                                            lineHeight: 32,
                                            width: 800,
                                            maxLines: 4,
                                            color: hexColor("#8E8E93"),
                                            fontFamily: "Roboto",
                                        }}
                                    >
                                        {selectedService().service_description}
                                    </Text>
                                </Show>
                            </View>
                        </Show>
                    </View>

                    {/* Services Carousel - At the bottom */}
                    <View y={850} x={80}>
                        <Show
                            when={!loading() && !error() && services().length > 0}
                            fallback={
                                <View x={700} y={50}>
                                    <Show when={loading()}>
                                        <LoadingIndicator />
                                    </Show>
                                    <Show when={error()}>
                                        <Text fontSize={28} color={hexColor("#ff6b6b")}>
                                            Error: {error()}
                                        </Text>
                                    </Show>
                                    <Show when={!loading() && !error() && services().length === 0}>
                                        <Text fontSize={28} color={hexColor("#7d7e82")}>
                                            No services available
                                        </Text>
                                    </Show>
                                </View>
                            }
                        >
                            <Text y={-50} skipFocus fontSize={30} style={{ fontWeight: "bold" }} marginBottom={20} color={hexColor("#ffffff")}>
                                Available Services
                            </Text>

                            <Row
                                ref={(el) => {
                                    rowRef = el;
                                    if (props.panelRef) props.panelRef.focusEntry = () => rowRef?.setFocus();
                                }}
                                gap={30}
                                scroll="always"
                                autofocus={true}
                                onUp={() => props.onUp?.()}
                            >
                                <For each={services()}>
                                    {(service) => (
                                        <View
                                            onFocus={() => handleServiceFocus(service)}
                                            onEnter={() => {
                                                console.log('Selected service:', service);
                                                // Navigate to service detail page
                                                // handleNavigate(`/service/${service._id}`);
                                            }}
                                            style={{
                                                // Jetstream-style card: subtle radius, thin focus outline, gentle scale
                                                width: 350,
                                                height: 180,
                                                scale: 1,
                                                borderRadius: 14,
                                                color: hexColor("#1a1a1a"),
                                                border: { width: 0, color: hexColor("#ffffff00") },
                                                transition: {
                                                    scale: { duration: 250, easing: "ease-in-out" },
                                                    border: { duration: 200 }
                                                },
                                                focus: {
                                                    scale: 1.05,
                                                    color: hexColor("#2a2a2a"),
                                                    border: { width: 4, color: hexColor("#ffffff") }
                                                }
                                            }}
                                        >
                                            {/* Service Image/Icon - Show image if available, otherwise show emoji icon */}
                                            <Show
                                                when={service.service_image || service.service_icon}
                                                fallback={
                                                    <View
                                                        x={125}
                                                        y={30}
                                                        style={{
                                                            width: 100,
                                                            height: 100,
                                                            borderRadius: 50,
                                                            color: hexColor("#3a3a3a"),
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}
                                                    >
                                                        <Text style={{ fontSize: 52 }}>
                                                            {getCategoryIcon(service.service_category)}
                                                        </Text>
                                                    </View>
                                                }
                                            >
                                                <View
                                                    // Through the app's image proxy: assets.innplay.cloud
                                                    // sends no CORS header, so WebGL draws the raw URL black
                                                    src={fixImageUrl(service.service_image || service.service_icon)}
                                                    x={125}
                                                    y={30}
                                                    width={100}
                                                    height={100}
                                                    style={{
                                                        borderRadius: 50,
                                                        objectFit: "cover"
                                                    }}
                                                />
                                            </Show>

                                            {/* Service Name */}
                                            <View y={145} x={20}>
                                                <Text
                                                    contain="width"
                                                    style={{
                                                        fontSize: 22,
                                                        color: hexColor("#ffffff"),
                                                        maxLines: 1,
                                                        width: 310,
                                                        textAlign: "center"
                                                    }}
                                                >
                                                    {service.service_name}
                                                </Text>
                                            </View>
                                        </View>
                                    )}
                                </For>
                            </Row>
                        </Show>
                    </View>
                </View>
            </View>
        </>
    );
};

export default ServicesPanel;
